import {
  Button,
  CircularProgress,
  FormControl,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { CheckCircle, Tune } from '@mui/icons-material'
import { enqueueSnackbar } from 'notistack'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  type Resolver,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { object, string } from 'yup'
import type { Device } from '@/pages/Device/Device'
import { RSS_3000_PC } from '@/pages/Device/SupportedTypes'
import {
  type ResistanceBox,
  resistanceBoxLabel,
  type RssCalibration,
} from '../calibration'
import type { Composition } from '../composition'
import { getRssCalibration, setRssConfiguration } from '@/api/calibration'
import useResponseValidator from '@/pages/utils/responseValidator'
import { RssCalibrationSchema } from '../schema'
import PreFlightStepBase from './PreFlightStepBase'
import {
  fieldLabelSpacedSx,
  formActionButtonDoneSx,
  formActionButtonSx,
  labelNoErrorColor,
  labelRoot,
  radioGroupLabelSx,
  requiredIndicator,
  selectStandardSx,
} from '@/utils/theme'
import { resolveDeviceRole, usePreFlightBase } from '@/hooks/usePreFlightBase'
import SharedPreFlightFields, {
  showFieldError,
} from '@/components/SharedPreFlightFields'

interface Props {
  device: Device
  setDevice?: (device: Device) => void
  composition: Composition | null
  setComposition: (c: Composition | null) => void
  resistanceBox: ResistanceBox | null
  setResistanceBox: (rb: ResistanceBox | null) => void
  setActiveStep: (step: number) => void
  onNext: () => void
  isActive?: boolean
}

type FormValues = {
  country: string
  deviceRoleCode: string
  testTool: string
}

interface RssExtraFieldsProps {
  deviceType: Device['type']
  resistanceBoxes: ResistanceBox[]
  configurationSet: boolean
  isSettingConfiguration: boolean
  onSetConfiguration: () => void
  onResistanceBoxChange: (label: string) => void
  isSubmitted: boolean
}

const RssExtraFields = ({
  deviceType,
  resistanceBoxes,
  configurationSet,
  isSettingConfiguration,
  onSetConfiguration,
  onResistanceBoxChange,
  isSubmitted,
}: RssExtraFieldsProps): JSX.Element => {
  const { t } = useTranslation('app')
  const { control } = useFormContext<FormValues>()

  let setConfigurationIcon = <Tune fontSize="small" />
  if (isSettingConfiguration) {
    setConfigurationIcon = <CircularProgress size={16} color="inherit" />
  } else if (configurationSet) {
    setConfigurationIcon = <CheckCircle fontSize="small" />
  }

  return (
    <>
      <FormGroup sx={{ mb: 2 }}>
        <Typography sx={radioGroupLabelSx}>
          {t('device.calibration.preFlightStep.rss.selectUsedTools')}
        </Typography>
        <Controller
          name="testTool"
          control={control}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              error={isSubmitted && Boolean(fieldState.error)}
              sx={{ mb: 2.5 }}
            >
              <InputLabel
                variant="standard"
                sx={{
                  ...labelRoot,
                  ...requiredIndicator,
                  ...labelNoErrorColor,
                }}
              >
                {t(
                  deviceType === RSS_3000_PC
                    ? 'device.calibration.rss.testToolPc'
                    : 'device.calibration.rss.testTool',
                )}
              </InputLabel>
              <Select
                {...field}
                variant="standard"
                sx={selectStandardSx}
                onChange={(e) => {
                  field.onChange(e)
                  onResistanceBoxChange(e.target.value)
                }}
              >
                {resistanceBoxes.map((rb) => (
                  <MenuItem key={rb.label} value={rb.label}>
                    {resistanceBoxLabel(rb)}
                  </MenuItem>
                ))}
              </Select>
              {isSubmitted && showFieldError(fieldState) && (
                <FormHelperText>{showFieldError(fieldState)}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      </FormGroup>

      <FormGroup sx={{ mb: 3 }}>
        <Typography sx={{ ...fieldLabelSpacedSx, ...requiredIndicator }}>
          {t('device.calibration.preFlightStep.rss.configuration')}
        </Typography>
        <Button
          variant="contained"
          onClick={onSetConfiguration}
          disabled={isSettingConfiguration || configurationSet}
          startIcon={setConfigurationIcon}
          data-testid="set-configuration-button"
          sx={{
            ...formActionButtonSx,
            ...(configurationSet ? formActionButtonDoneSx : {}),
          }}
        >
          {t(
            configurationSet
              ? 'device.calibration.preFlightStep.rss.configurationSet'
              : 'device.calibration.preFlightStep.rss.setConfiguration',
          )}
        </Button>
      </FormGroup>
    </>
  )
}

const RssPreFlightStep = ({
  device,
  setDevice,
  setComposition,
  resistanceBox,
  setResistanceBox,
  setActiveStep,
  onNext,
  isActive = true,
}: Props): JSX.Element => {
  const { t } = useTranslation('app')
  const { validate } = useResponseValidator()
  const [resistanceBoxes, setResistanceBoxes] = useState<ResistanceBox[]>([])
  const [configurationSet, setConfigurationSet] = useState(false)
  const [isSettingConfiguration, setIsSettingConfiguration] = useState(false)

  const {
    composition,
    refreshComposition,
    deviceRoles,
    deviceRolesLoading,
    isSaving,
    effectiveCountry,
    countryItems,
    minInstalledDate,
    deviceRoleRequired,
    submitForm,
    handleCountryChange,
    handleYearsIntervalChange,
  } = usePreFlightBase(device, setComposition, onNext)

  const compositionDeviceRoleCode =
    composition?.composition?.deviceRoleCode ?? ''

  const validateRef = useRef(validate)
  useEffect(() => {
    validateRef.current = validate
  })

  // Temporary bridge until the static check is fully rewritten: the
  // configuration must be re-sent on every visit to this screen, so the
  // done-state resets whenever the step becomes active again.
  useEffect(() => {
    if (isActive) setConfigurationSet(false)
  }, [isActive])

  const fetchCalibration = useCallback(async () => {
    try {
      const response = await getRssCalibration(device.uid)
      const { resistances } = await validateRef.current<RssCalibration>(
        RssCalibrationSchema,
        response,
      )
      setResistanceBoxes(resistances)
    } catch {
      /* Caught by interceptor */
    }
  }, [device.uid])

  useEffect(() => {
    void fetchCalibration()
  }, [fetchCalibration])

  const handleResistanceBoxChange = useCallback(
    (label: string) => {
      const found = resistanceBoxes.find((rb) => rb.label === label)
      if (found) setResistanceBox(found)
    },
    [resistanceBoxes, setResistanceBox],
  )

  const schema = useMemo(
    () =>
      object({
        country: string().required(),
        deviceRoleCode: deviceRoleRequired
          ? string().required(
              t('device.calibration.preFlightStep.deviceRoleRequired'),
            )
          : string().optional(),
        testTool: string().required(),
      }),
    [deviceRoleRequired, t],
  )

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as Resolver<FormValues>,
    defaultValues: {
      country: effectiveCountry,
      deviceRoleCode: compositionDeviceRoleCode,
      testTool: resistanceBox?.label ?? '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
  })

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const handleSetConfiguration = useCallback(async () => {
    setIsSettingConfiguration(true)
    try {
      await setRssConfiguration(device.uid)
      setConfigurationSet(true)
      enqueueSnackbar(
        t('device.calibration.preFlightStep.rss.configUpdateSuccess'),
        { variant: 'success' },
      )
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : t('device.calibration.preFlightStep.rss.configUpdateError'),
        { variant: 'error' },
      )
    } finally {
      setIsSettingConfiguration(false)
    }
  }, [device.uid, t])

  useEffect(() => {
    if (deviceRoles.length === 0) return
    methods.setValue(
      'deviceRoleCode',
      resolveDeviceRole(deviceRoles, compositionDeviceRoleCode),
      { shouldValidate: true },
    )
  }, [compositionDeviceRoleCode, deviceRoles, methods])

  const { serial, label, type } = device

  return (
    <FormProvider {...methods}>
      <PreFlightStepBase
        serial={serial ?? ''}
        label={label}
        type={type}
        isSaving={isSaving}
        nextDisabled={!configurationSet}
        composition={composition}
        onRefreshComposition={refreshComposition}
        onBack={() => setActiveStep(0)}
        onFormSubmit={(e, compositionCheckPasses) => {
          setHasAttemptedSubmit(true)
          void methods.handleSubmit(async (values) => {
            if (!compositionCheckPasses) return
            // The configuration has to be set and confirmed by the device
            // before the self test can start; guarded here as well as on the
            // Next button, so that submitting the form another way (pressing
            // enter in a field) cannot skip it either.
            if (!configurationSet) return
            await submitForm({
              country: values.country,
              deviceRoleCode: values.deviceRoleCode,
            })
          })(e)
        }}
      >
        <SharedPreFlightFields
          control={methods.control}
          isSubmitted={hasAttemptedSubmit}
          hasBoard={false}
          minInstalledDate={minInstalledDate}
          countryItems={countryItems}
          deviceRoles={deviceRoles}
          deviceRolesLoading={deviceRolesLoading}
          hasConfigurableServiceInterval={false}
          isSaving={isSaving}
          onCountryChange={handleCountryChange}
          onYearsIntervalChange={handleYearsIntervalChange}
          device={device}
          setDevice={setDevice}
        />
        <RssExtraFields
          deviceType={device.type}
          resistanceBoxes={resistanceBoxes}
          configurationSet={configurationSet}
          isSettingConfiguration={isSettingConfiguration}
          onSetConfiguration={() => void handleSetConfiguration()}
          onResistanceBoxChange={handleResistanceBoxChange}
          isSubmitted={hasAttemptedSubmit}
        />
      </PreFlightStepBase>
    </FormProvider>
  )
}

export default RssPreFlightStep
